import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  message,
  Modal,
  Row,
  Col,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { stockService } from '../../services/stockService';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface StockOutVoucher {
  id: number;
  voucher_number: string;
  customer_name: string;
  customer_phone: string;
  total_amount: number;
  status: string;
  completed_at: string;
  created_by_name: string;
  created_at: string;
}

const StockOutCleanupTool: React.FC = () => {
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);

  const queryClient = useQueryClient();

  const { data: vouchers = [], isFetching, refetch } = useQuery(
    ['tools-stock-out-completed', search, dateRange[0]?.valueOf(), dateRange[1]?.valueOf()],
    () => stockService.getStockOutVouchers({
      search: search || undefined,
      status: 'completed',
      start_date: dateRange[0]?.format('YYYY-MM-DD'),
      end_date: dateRange[1]?.format('YYYY-MM-DD')
    }),
    {
      keepPreviousData: true
    }
  );

  const deleteMutation = useMutation(
    (id: number) => stockService.forceDeleteStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Đã xóa phiếu xuất kho và hoàn trả tồn kho');
        queryClient.invalidateQueries('tools-stock-out-completed');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Không thể xóa phiếu xuất kho');
      }
    }
  );

  const handleForceDelete = (record: StockOutVoucher) => {
    Modal.confirm({
      title: `Xóa cưỡng bức phiếu ${record.voucher_number}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Hành động này sẽ:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li>Hoàn trả tồn kho theo từng lô đã xuất</li>
            <li>Xóa công nợ và thanh toán liên quan (nếu có)</li>
            <li>Không thể hoàn tác</li>
          </ul>
        </div>
      ),
      okText: 'Xóa cưỡng bức',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: () => deleteMutation.mutate(record.id)
    });
  };

  const stats = useMemo(() => {
    return {
      total: vouchers.length,
      totalAmount: vouchers.reduce((sum: number, v: StockOutVoucher) => sum + (v.total_amount || 0), 0)
    };
  }, [vouchers]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        type="warning"
        showIcon
        message="Công cụ xóa phiếu xuất kho đã hoàn thành"
        description="Chỉ sử dụng khi cần xoá phiếu sai sót đã hoàn thành. Hệ thống sẽ hoàn trả tồn kho đúng từng lô và xoá toàn bộ công nợ, thanh toán liên quan."
      />

      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={10}>
              <Input.Search
                placeholder="Tìm theo mã phiếu, khách hàng..."
                allowClear
                enterButton="Tìm kiếm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onSearch={() => refetch()}
              />
            </Col>
            <Col xs={24} md={10}>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={(values) => setDateRange(values as [Dayjs | null, Dayjs | null])}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} md={4} style={{ textAlign: 'right' }}>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
                Làm mới
              </Button>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Statistic title="Tổng số phiếu" value={stats.total} />
            </Col>
            <Col xs={24} md={12}>
              <Statistic
                title="Tổng giá trị"
                value={stats.totalAmount}
                precision={0}
                suffix="VNĐ"
              />
            </Col>
          </Row>

          <Table<StockOutVoucher>
            rowKey="id"
            loading={isFetching || deleteMutation.isLoading}
            dataSource={vouchers}
            pagination={{ pageSize: 10 }}
            columns={[
              {
                title: 'Mã phiếu',
                dataIndex: 'voucher_number',
                render: (text: string) => <Text strong>{text}</Text>
              },
              {
                title: 'Khách hàng',
                dataIndex: 'customer_name',
                render: (_: any, record) => (
                  <div>
                    <div>{record.customer_name || 'N/A'}</div>
                    <Text type="secondary">{record.customer_phone}</Text>
                  </div>
                )
              },
              {
                title: 'Tổng tiền',
                dataIndex: 'total_amount',
                render: (value: number) => (
                  <Text>{(value || 0).toLocaleString('vi-VN')} ₫</Text>
                )
              },
              {
                title: 'Trạng thái',
                dataIndex: 'status',
                render: () => <Tag color="green">completed</Tag>
              },
              {
                title: 'Hoàn thành lúc',
                dataIndex: 'completed_at',
                render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : 'Chưa rõ')
              },
              {
                title: 'Người tạo',
                dataIndex: 'created_by_name'
              },
              {
                title: 'Thao tác',
                dataIndex: 'actions',
                render: (_: any, record) => (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleForceDelete(record)}
                  >
                    Xóa cưỡng bức
                  </Button>
                )
              }
            ]}
          />
        </Space>
      </Card>
    </Space>
  );
};

export default StockOutCleanupTool;

